import { Trans, useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { CustomDialog } from "@medoma/components/Dialog/Dialog";
import { Text } from "@medoma/components/Text/Text";
import { parseISO } from "date-fns";
import { humanFormat } from "@medoma/models/date-and-time";
import { daysAgo } from "@/Utils/DateUtils";
import type { IExistingPatient } from "@medoma/models/patients";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
} from "@medoma/models/patients";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import styles from "./Pause.module.scss";

export const PauseReview = ({
  patient,
  pauseDate,
  activitiesToCloseCount,
  activitiesToKeepCount,
  disableSubmitButton,
  onCancel,
  onPause,
}: {
  patient: IExistingPatient;
  pauseDate: string;
  activitiesToCloseCount: number;
  activitiesToKeepCount: number;
  disableSubmitButton: boolean;
  onCancel: () => void;
  onPause: () => void;
}) => {
  const { t } = useLingui();
  const patientName = getPatientFullName(patient.name);
  const date = parseISO(pauseDate);

  return (
    <CustomDialog
      isOpen={true}
      onClose={onCancel}
      title={t`Granska paus`}
      preferredWidth={720}
      actions={
        <>
          <OutlinedButton onClick={onCancel}>{t`Avbryt`}</OutlinedButton>
          <FilledButton disabled={disableSubmitButton} onClick={onPause}>
            {t`Pausa`}
          </FilledButton>
        </>
      }
    >
      <div className={styles.review}>
        <DescriptionList>
          <DescriptionList.Term>
            <Trans>Patient</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            {patientName}
          </DescriptionList.Description>
          <DescriptionList.Term>
            <Trans>Personnummer</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            {formatPersonalIdentityNumber({
              personalIdentityNumber: patient.personalIdentityNumber,
              personalIdentityNumberType: patient.personalIdentityNumberType,
            })}
          </DescriptionList.Description>
          <DescriptionList.Term>
            <Trans>Pausad från</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            <Text element="span" weight="medium">
              {humanFormat(date, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Text>{" "}
            <Text element="span" color="faded">
              ({daysAgo(date)})
            </Text>
          </DescriptionList.Description>
          {activitiesToCloseCount > 0 || activitiesToKeepCount > 0 ? (
            <>
              <DescriptionList.Term>
                <Trans>Aktiviteter</Trans>
              </DescriptionList.Term>
              <DescriptionList.Description>
                <ul className={styles.consequences}>
                  {activitiesToCloseCount > 0 ? (
                    <li>
                      {plural(activitiesToCloseCount, {
                        one: "# aktivitet avslutas",
                        other: "# aktiviteter avslutas",
                      })}
                    </li>
                  ) : null}
                  {activitiesToKeepCount > 0 ? (
                    <li>
                      {plural(activitiesToKeepCount, {
                        one: "# aktivitet fortsätter",
                        other: "# aktiviteter fortsätter",
                      })}
                    </li>
                  ) : null}
                </ul>
              </DescriptionList.Description>
            </>
          ) : null}
        </DescriptionList>
        <Text element="p">
          <Trans>
            <Text element="span" weight="bold">
              Vårdperioden är pausad tills den återupptas.
            </Text>{" "}
            Aktiviteter som avslutas kan skapas igen automatiskt vid
            återupptagning.
          </Trans>
        </Text>
      </div>
    </CustomDialog>
  );
};
